/**
 * engine/sync.ts — the queue's copy on the server: read once at launch, written after changes.
 *
 * Task 08. `spec.md` «Плеер»: the queue is «локальная с сохранением на сервере (переживает
 * перезапуск и смену телефона)». The queue lives in `store/queue.ts`; this module is the only
 * thing that talks to `GET` and `PUT /api/queue` about it.
 *
 * ## When a save goes out
 *
 * - **After an edit**, once the listener has stopped editing for {@link SAVE_DEBOUNCE_MS}: a
 *   drag across ten rows is one save, not ten. An edit is anything that moves the queue's
 *   `revision` — including the track changing.
 * - **At once, on `flush()`**: the player calls it on a pause, at the end of the queue, when
 *   the app is minimised and every ten seconds of play, so the second a listener stopped at is
 *   on the server even if the app is closed rather than minimised.
 *
 * Each save carries the whole queue and the player's second, read at the moment it is sent,
 * never at the moment it was asked for: a save that waited carries the newest state. A queue
 * that ended is saved at second zero — a track held at the last second of a finished track
 * would end at the first «Играть».
 *
 * ## Nothing unchanged is sent
 *
 * A save identical to the last one that landed — or to what was read at launch — is not sent.
 * Besides saving requests, this is what keeps a phone left open and untouched from
 * overwriting, as it is minimised, the queue the listener has since built on another phone:
 * «the last save wins» must mean the phone used last, not the phone touched last.
 *
 * ## One save in flight
 *
 * Two saves of one phone racing each other could land in the wrong order and leave the older
 * state on the server. So a save asked for while another is in flight waits for it and then
 * goes out once, with whatever the state is by then.
 *
 * ## Nothing is sent before the saved queue has been read
 *
 * At launch the queue is empty, and an empty queue saved in that first second — the app is
 * minimised while the request is out, say — would overwrite the listener's real one on the
 * server. So nothing goes out until either the saved queue has been read, or the listener has
 * changed something themselves: their own choice outranks what was saved, even when the read
 * failed. And a read that comes back after the listener has already tapped a track is dropped:
 * the tap is newer.
 *
 * ## The place in a long track goes with it (task 49)
 *
 * Every save also hands the second of the track playing to `PUT /api/positions/:id` — when the
 * track is long (`engine/positions.ts`), when it has played in this session, and when the second
 * moved since the last place sent or read. «Played in this session» is what keeps an untouched
 * phone, holding a restored mix at yesterday's second, from writing that second over the place
 * another phone has moved it to since — the same reason nothing unchanged of the queue is sent.
 */

import { fetchQueue, saveQueue } from '../lib/api';
import type { ApiResult, QueueAnswer, QueueSave } from '../lib/api';
import { usePlayer } from '../store/player';
import { useQueue } from '../store/queue';
import { useSession } from '../store/session';

import { isLongTrack, placeOf, sendPlace } from './positions';

/** Quiet time after the last edit before it is saved. */
export const SAVE_DEBOUNCE_MS = 1_000;

/** The server refuses a position past a day (`routes/queue.ts`); so does this. */
const MAX_POSITION_S = 86_400;

export interface SyncDeps {
  readonly load: () => Promise<ApiResult<QueueAnswer>>;
  readonly save: (body: QueueSave) => Promise<ApiResult<{ readonly updatedAt: number }>>;
  /** Task 49: the place in a long track; `true` when the server has it. Absent — not sent. */
  readonly savePlace?: ((trackId: number, positionS: number) => Promise<boolean>) | undefined;
}

export interface QueueSync {
  /**
   * Reads the saved queue into the store, once for the life of the page. Resolves with the
   * second to hold the current track at, or `undefined` when nothing was restored.
   */
  restore(): Promise<number | undefined>;
  /** Saves now. Does not wait. */
  flush(): void;
  dispose(): void;
}

/** What goes to the server: the queue as it is, and the player's second if it holds the current track. */
function snapshot(): QueueSave {
  const queue = useQueue.getState();
  const current = queue.entries[queue.index];
  const player = usePlayer.getState();
  const holdsCurrent = current !== undefined && player.trackId === current.track.id;
  const positionS =
    holdsCurrent && player.status !== 'ended'
      ? Math.min(MAX_POSITION_S, Math.max(0, Math.floor(player.positionS)))
      : 0;
  return {
    trackIds: queue.entries.map((entry) => entry.track.id),
    index: queue.index,
    shuffle: queue.shuffle,
    shuffleSeed: queue.seed,
    shuffleAnchor: queue.anchor,
    repeat: queue.repeat,
    positionS,
    sourceKey: queue.source?.key ?? null,
    sourceCursor: queue.source?.cursor ?? null,
    pinned: queue.pinned,
    freshFrom: queue.freshFrom,
    transition: queue.transition,
  };
}

export function createSync(deps: SyncDeps): QueueSync {
  let restoring: Promise<number | undefined> | undefined;
  let restored = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let inFlight = false;
  let again = false;
  let disposed = false;
  /** The body of the last save that landed, or of what was read at launch. */
  let lastSent: string | undefined;

  const allowed = (): boolean => restored || useQueue.getState().revision > 0;

  /** Task 49: the tracks that have played since the sync started. */
  const heard = new Set<number>();
  const stopHearing = usePlayer.subscribe((state) => {
    if (state.status === 'playing' && state.trackId !== undefined) heard.add(state.trackId);
  });

  /** The place of the long track playing, if it moved — see the header. */
  async function sendPlaceOfCurrent(): Promise<void> {
    const player = usePlayer.getState();
    const track = player.track;
    if (deps.savePlace === undefined || track === undefined || player.status === 'ended') return;
    if (!isLongTrack(track.durationS) || !heard.has(track.id)) return;
    const positionS = Math.max(0, Math.floor(player.positionS));
    if (placeOf(track.id) === positionS) return;
    await deps.savePlace(track.id, positionS);
  }

  async function send(): Promise<void> {
    if (disposed || !allowed()) return;
    if (inFlight) {
      again = true;
      return;
    }
    const body = snapshot();
    const text = JSON.stringify(body);
    if (text === lastSent) {
      await sendPlaceOfCurrent().catch(() => undefined);
      return;
    }
    inFlight = true;
    try {
      // A failure is not retried here: the next edit or the next ten seconds of play carry
      // the same state, newer — and `lastSent` is left alone, so they are not taken for
      // duplicates of something that never landed.
      const result = await deps.save(body);
      if (result.ok) lastSent = text;
      await sendPlaceOfCurrent();
    } catch {
      // A save is best effort; nothing on screen depends on it having landed.
    } finally {
      inFlight = false;
    }
    if (again) {
      again = false;
      await send();
    }
  }

  function flush(): void {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    void send();
  }

  const unsubscribe = useQueue.subscribe((state, previous) => {
    if (state.revision === previous.revision || disposed) return;
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(flush, SAVE_DEBOUNCE_MS);
  });

  async function readSaved(): Promise<number | undefined> {
    let result: ApiResult<QueueAnswer>;
    try {
      result = await deps.load();
    } catch {
      return undefined;
    }
    if (!result.ok || disposed) return undefined;
    if (useQueue.getState().revision > 0) {
      // The listener chose something while the request was out: theirs is newer.
      restored = true;
      return undefined;
    }
    const positionS = useQueue.getState().hydrate(result.data);
    restored = true;
    // What the server holds, in the shape a save would have: the shell holds the current
    // track at this second next, and an untouched copy of it is not worth sending back.
    lastSent = JSON.stringify({ ...snapshot(), positionS: positionS ?? 0 });
    return positionS;
  }

  return {
    restore: () => {
      restoring ??= readSaved();
      return restoring;
    },
    flush,
    dispose: () => {
      disposed = true;
      unsubscribe();
      stopHearing();
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    },
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * The instance the app uses
 *
 * It runs for as long as the shell is on screen: `app/Shell.tsx` starts it on mount and stops
 * it on unmount. Nothing else starts one, so a screen that never drew the shell — the
 * «subscribe» screen, a test of the launch — never sends a save; and a test that unmounts the
 * shell leaves no timer behind to fire into the next one.
 * ──────────────────────────────────────────────────────────────────────────── */

function token(): string | undefined {
  return useSession.getState().session?.token;
}

const LIVE_DEPS: SyncDeps = {
  load: async () => await fetchQueue(token()),
  save: async (body) => await saveQueue(token(), body),
  savePlace: sendPlace,
};

let running: QueueSync | undefined;

/**
 * Starts a sync — with the real requests, or with `deps`, the web suite's seam — and stops the
 * one running before it.
 */
export function startSync(deps: SyncDeps = LIVE_DEPS): QueueSync {
  running?.dispose();
  running = createSync(deps);
  return running;
}

/** The sync the shell started, while it runs. */
export function runningSync(): QueueSync | undefined {
  return running;
}

/** Stops `sync`; the one running only if it is that one. */
export function stopSync(sync: QueueSync): void {
  sync.dispose();
  if (running === sync) running = undefined;
}
